import { useEffect, useMemo, useRef, useState } from 'react'
import { LocationOn } from '@mui/icons-material'
import { Autocomplete, debounce, Grid, TextField, Typography } from '@mui/material'
import { useFormikContext } from 'formik'

import { GOOGLE_PLACES_API_KEY } from '../../utils/constants'
import useSharedStyles from '../../utils/shared.styles'

import { fetchAddressInfo, geocoderService, loadScript } from './helpers'
import useStyles from './index.styles'

const FormikAddress = () => {
  const classes = useStyles
  const sharedClasses = useSharedStyles

  const formikBag = useFormikContext<AddressFormValues>()
  const [value, setValue] = useState<string | google.maps.places.PlacePrediction | null>(null)
  const [options, setOptions] = useState<google.maps.places.PlacePrediction[]>([])
  const [inputValue, setInputValue] = useState('')
  const sessionToken = useRef<google.maps.places.AutocompleteSessionToken | null>(null)

  useEffect(() => {
    setValue(formikBag.initialValues.address)
    setInputValue(formikBag.initialValues.address)
  }, [formikBag.initialValues.address])

  const googleServicesLoaded = useRef<boolean>(false)

  if (typeof window !== 'undefined' && !googleServicesLoaded.current) {
    if (!document.querySelector('#google-maps')) {
      loadScript(`https://maps.googleapis.com/maps/api/js?key=${GOOGLE_PLACES_API_KEY}&libraries=places`, document.querySelector('head'), 'google-maps')
    }

    googleServicesLoaded.current = true
  }

  const fetch = useMemo(
    () =>
      debounce(async (request: google.maps.places.AutocompleteRequest, callback: (results: google.maps.places.PlacePrediction[]) => void) => {
        try {
          if (!window.google?.maps?.places?.AutocompleteSuggestion) {
            callback([])
            return
          }

          const { suggestions } = await window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request)
          const placePredictions = suggestions
            .map((suggestion) => suggestion.placePrediction)
            .filter((prediction): prediction is google.maps.places.PlacePrediction => Boolean(prediction))

          callback(placePredictions)
        } catch {
          callback([])
        }
      }, 400),
    []
  )

  useEffect(() => {
    let active = true

    if (!geocoderService.current && window.google) {
      geocoderService.current = new window.google.maps.Geocoder()
      // if the service is _still_ undefined, abort, we can't do anything
      if (!geocoderService.current) return undefined
    }

    if (inputValue === '') {
      setOptions([])
      return undefined
    }

    if (!sessionToken.current && window.google?.maps?.places?.AutocompleteSessionToken) {
      sessionToken.current = new window.google.maps.places.AutocompleteSessionToken()
    }

    fetch({ input: inputValue, sessionToken: sessionToken.current ?? undefined }, (results: google.maps.places.PlacePrediction[]) => {
      if (active) {
        setOptions(results)
      }
    })

    return () => {
      active = false
    }
  }, [inputValue, fetch])

  return (
    <Autocomplete
      getOptionLabel={(option) => (typeof option === 'string' ? option : option.text.toString())}
      filterOptions={(x) => x}
      options={options}
      freeSolo={true}
      autoComplete={true}
      filterSelectedOptions={true}
      fullWidth={true}
      value={value === '' ? null : value} // mui shows the "clear" icon if the value is an empty string, we don't want the clear icon
      onChange={async (_, address: string | google.maps.places.PlacePrediction | null) => {
        setValue(address)

        if (!address) return
        if (typeof address === 'string') {
          formikBag.setFieldValue('address', address)
          sessionToken.current = null
        } else {
          const formattedAddress = await fetchAddressInfo(address.placeId)

          if (formattedAddress) {
            setInputValue(formattedAddress.address)
            setValue(formattedAddress.address)
            formikBag.setFieldValue('address', formattedAddress.address)
            formikBag.setFieldValue('address2', formattedAddress.address2)
            formikBag.setFieldValue('region', formattedAddress.region)
            formikBag.setFieldValue('locality', formattedAddress.locality)
            formikBag.setFieldValue('code', formattedAddress.code)
            formikBag.setFieldValue('country', formattedAddress.country)
            sessionToken.current = null
          }
        }
      }}
      onInputChange={(_, newInputValue) => {
        setInputValue(newInputValue)
        formikBag.setFieldValue('address', newInputValue)
      }}
      renderInput={(params) => <TextField {...params} margin="normal" label="Address" fullWidth={true} />}
      renderOption={(props, option) => {
        const { key, ...rest } = props
        return (
          <li key={key} {...rest}>
            <Grid container={true} sx={sharedClasses.centerContentVertical}>
              <Grid sx={classes.iconContainer}>
                <LocationOn sx={sharedClasses.secondaryText} />
              </Grid>
              <Grid sx={classes.textContainer}>
                {option.text.toString()}
                <Typography variant="body2" sx={sharedClasses.secondaryText}>
                  {option.secondaryText?.toString()}
                </Typography>
              </Grid>
            </Grid>
          </li>
        )
      }}
    />
  )
}

export default FormikAddress
